import { ThreeEvent } from '@react-three/fiber';
import React, { useMemo } from 'react';
import * as THREE from 'three';

import { EWall } from '../../@types/Enums';
import { IBoundaryEdge } from '../../models/abstract/BoundaryEdge';
import { IWall } from '../../models/abstract/Wall';
import FloorModel from '../../models/standard/FloorModel';
import PhysicalBoundaryModel from '../../models/standard/PhysicalBoundary';
import RoomModel from '../../models/standard/RoomModel';
import { ISpace } from '../../models/standard/SpaceModel';
import StandardWallModel from '../../models/standard/StandardWallModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useSelection } from '../../provider/SelectionProvider';
import {
  calculateBoundaryIndicatorSegments,
  calculateBoundaryPolygonOrientation,
  calculateInsetFloorPolygonForOrderedEdges,
  orderBoundaryEdges,
} from '../../utils/boundaryGeometryUtils';
import { UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import { updateEdgesWithFloorGeometry } from '../../utils/spaceUtils';
import Floor from './Floor';
import ElementDistanceIndicator from './indicators/ElementDistanceIndicator';
import InnerWallLengthIndicator from './indicators/InnerWallLengthIndicator';
import WallAngleIndicator from './indicators/WallAngleIndicator';

const EPS = 1e-4;
const samePt = (a: THREE.Vector3, b: THREE.Vector3, eps = EPS) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

function toStdWalls(walls: IWall[]): StandardWallModel[] {
  return (walls || []).filter(Boolean).filter((w) => w.type === EWall.STANDARD) as StandardWallModel[];
}

/** ---------- Geometry Helpers ---------- */
function orderWallsToPolyline(walls: StandardWallModel[]): THREE.Vector3[] {
  const ws = walls.filter(Boolean);
  if (ws.length === 0) return [];
  const used = new Array(ws.length).fill(false);
  function tryChain(reverseFirst: boolean): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    let count = 0;
    const w0 = ws[0];
    const currStart = reverseFirst ? w0.endPoint : w0.startPoint;
    let currEnd = reverseFirst ? w0.startPoint : w0.endPoint;
    pts.push(currStart.clone(), currEnd.clone());
    used[0] = true;
    count++;
    while (count < ws.length) {
      let extended = false;
      for (let i = 1; i < ws.length; i++) {
        if (used[i]) continue;
        const w = ws[i];
        if (samePt(currEnd, w.startPoint)) {
          pts.push(w.endPoint.clone());
          currEnd = w.endPoint;
          used[i] = true;
          count++;
          extended = true;
          break;
        }
        if (samePt(currEnd, w.endPoint)) {
          pts.push(w.startPoint.clone());
          currEnd = w.startPoint;
          used[i] = true;
          count++;
          extended = true;
          break;
        }
      }
      if (!extended) break;
    }
    return pts;
  }
  let pts = tryChain(false);
  if (pts.length - 1 < ws.length) {
    used.fill(false);
    pts = tryChain(true);
  }
  return pts;
}
function toClockwise(points: THREE.Vector3[]): THREE.Vector3[] {
  if (points.length < 3) return points;
  let area = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i],
      b = points[i + 1];
    area += a.x * b.y - b.x * a.y;
  }
  const a0 = points[0],
    b0 = points[points.length - 1];
  area += b0.x * a0.y - a0.x * b0.y;
  return area > 0 ? [...points].reverse() : points;
}
function shapeFromPolyline(points: THREE.Vector3[]): THREE.Shape {
  const shape = new THREE.Shape();
  if (points.length < 3) return shape;
  const first = points[0];
  const last = points[points.length - 1];
  const pts = samePt(first, last) ? points : [...points, first];
  const cw = toClockwise(pts);
  shape.moveTo(cw[0].x, cw[0].y);
  for (let i = 1; i < cw.length; i++) shape.lineTo(cw[i].x, cw[i].y);
  shape.closePath();
  return shape;
}
function resolveFloorModel(_building: any, _roomModel: RoomModel, spaceModel?: ISpace | null): FloorModel {
  return spaceModel?.floor;
}

export type Props = {
  roomModel: RoomModel;
  spaceModel?: ISpace | null;
  onRoomClick?: () => void;
  onFloorPointerDown?: (shape: THREE.Shape) => (e: ThreeEvent<PointerEvent>) => void;
  onFloorPointerMove?: (shape: THREE.Shape) => (e: ThreeEvent<PointerEvent>) => void;
};

const Space: React.FC<Props> = ({ roomModel, spaceModel, onRoomClick, onFloorPointerDown, onFloorPointerMove }) => {
  const { building } = useBuilding();
  const { selectedSpace, selectedStandardWall } = useSelection();
  const { showAngles, showElementDistances, showWallInnerLength } = useMultiSelectToggleContext();

  const primaryBoundary = useMemo(() => {
    const arr = Array.isArray(spaceModel?.boundaries) ? spaceModel!.boundaries : [];
    return (arr.find((b) => b instanceof PhysicalBoundaryModel) as PhysicalBoundaryModel | undefined) ?? undefined;
  }, [spaceModel?.boundaries]);

  const edges = useMemo(() => (primaryBoundary?.edges ?? []).filter(Boolean) as IBoundaryEdge[], [primaryBoundary?.edges]);

  const stdWalls = useMemo(() => {
    const wallsArr = Array.isArray(spaceModel?.walls) ? (spaceModel!.walls as IWall[]).filter(Boolean) : [];
    const wallUuids = wallsArr.map((w) => w.uuid);
    const liveWalls = building.walls.filter((w) => wallUuids.includes(w.uuid));
    return toStdWalls(liveWalls);
  }, [spaceModel?.walls, building.walls]);

  const elementsHash = useMemo(() => {
    if (!showElementDistances) return '';

    return stdWalls
      .map((w) => {
        const wSig = (w.windows || []).map((win) => `${win.position.x},${win.position.y},${win.length}`).join(';');
        const dSig = (w.doors || []).map((door) => `${door.position.x},${door.position.y},${door.length}`).join(';');
        const pSig = (w.passages || []).map((passage) => `${passage.position.x},${passage.position.y},${passage.length}`).join(';');

        return `${w.uuid}[${wSig}][${dSig}][${pSig}]`;
      })
      .join('|');
  }, [stdWalls, showElementDistances]);

  const orderedEdges = useMemo(() => orderBoundaryEdges(edges), [edges, stdWalls]);

  const polygonOrientation = useMemo(() => calculateBoundaryPolygonOrientation(orderedEdges), [orderedEdges]);

  const isWallSharedLocally = (wallUuid: string) => {
    let count = 0;
    for (const r of building?.rooms ?? []) {
      for (const s of r.spaces ?? []) {
        const primary = (s.boundaries ?? []).find((b: any) => b instanceof PhysicalBoundaryModel) as any;
        if (primary?.edges?.some((e: any) => e.wall?.uuid === wallUuid)) {
          count++;
          break; // Wand in diesem Raum gefunden, weiter zum nächsten Raum
        }
      }
    }
    return count > 1; // True, wenn mehr als 1 Raum die Wand teilt
  };

  const shape = useMemo(() => {
    if (orderedEdges.length >= 3) {
      const success = updateEdgesWithFloorGeometry(orderedEdges);
      if (!success) {
        orderedEdges.forEach((o) => {
          if (typeof o.edge.resetPoints === 'function') o.edge.resetPoints();
        });
      }

      const insetPolygonPoints = calculateInsetFloorPolygonForOrderedEdges(orderedEdges, polygonOrientation);

      if (insetPolygonPoints) {
        if (primaryBoundary && primaryBoundary.edges) {
          const newEdgeOrder = orderedEdges.map((o) => o.edge);
          if (primaryBoundary.edges.length !== newEdgeOrder.length || primaryBoundary.edges.some((e, i) => e !== newEdgeOrder[i])) {
            primaryBoundary.edges = newEdgeOrder;
          }
        }

        // 2. Erstelle das Shape direkt aus den berechneten Punkten!
        return shapeFromPolyline(insetPolygonPoints);
      }
    }

    if (stdWalls.length >= 3) {
      const polyline = orderWallsToPolyline(stdWalls);
      if (polyline.length >= 3) {
        if (!samePt(polyline[0], polyline[polyline.length - 1])) {
          polyline.push(polyline[0].clone());
        }

        return shapeFromPolyline(polyline);
      }
    }

    return new THREE.Shape();
  }, [orderedEdges, stdWalls, primaryBoundary, polygonOrientation]);

  const indicatorData = useMemo(() => {
    if (!showAngles && !showWallInnerLength && !showElementDistances) return [];
    if (orderedEdges.length < 2 || !spaceModel || !spaceModel.uuid) return [];

    const insetSegments = calculateBoundaryIndicatorSegments(orderedEdges, polygonOrientation);

    return orderedEdges.map((seg, index) => {
      const prev = orderedEdges[(index - 1 + orderedEdges.length) % orderedEdges.length];

      let startPoint = seg.s.clone();
      let endPoint = seg.e.clone();
      const insetSegment = insetSegments[index];

      if (insetSegment) {
        startPoint = insetSegment.startPoint.clone();
        endPoint = insetSegment.endPoint.clone();
      }

      if (seg.edge && typeof (seg.edge as any).setPoints === 'function') {
        (seg.edge as any).setPoints(startPoint.clone(), endPoint.clone());
      }

      const segWall = stdWalls.find((sW) => sW.uuid === seg.edge.wall.uuid);

      const isSelected = segWall ? building.walls.findIndex((w) => w.uuid === segWall.uuid) === selectedStandardWall?.wallIndex : false;

      const hasWindowsDoorsOrPassages = showElementDistances && ((segWall?.windows?.length ?? 0) > 0 || (segWall?.doors?.length ?? 0) > 0 || (segWall?.passages?.length ?? 0) > 0);

      const visualSide = seg.edge.side || (seg.edge.reversed ? 'B' : 'A');

      const inwardNormal = hasWindowsDoorsOrPassages
        ? (() => {
            const dir = new THREE.Vector3().subVectors(endPoint, startPoint).normalize();
            return polygonOrientation === 1 ? new THREE.Vector3(-dir.y, dir.x, 0) : new THREE.Vector3(dir.y, -dir.x, 0);
          })()
        : undefined;

      return {
        key: `edge-${spaceModel.uuid}-${index}`,
        prevEdge: prev.edge,
        nextEdge: seg.edge,
        innerCorner: startPoint,
        startPoint,
        endPoint,
        wallSide: visualSide,
        wallSideALength: startPoint.distanceTo(endPoint) / UNIT_TO_METER_DIVISOR,
        shiftIndicator: showElementDistances && hasWindowsDoorsOrPassages,
        isWallSelected: isSelected,
        hasElements: hasWindowsDoorsOrPassages,
        segWall,
        inwardNormal,
      };
    });
  }, [orderedEdges, polygonOrientation, stdWalls, building, showAngles, showElementDistances, showWallInnerLength, selectedStandardWall, elementsHash]);

  if (!spaceModel) return null;
  if (shape.getPoints().length === 0) return null;

  const floorModel = resolveFloorModel(building, roomModel, spaceModel);
  const selected = selectedSpace?.spaceId === spaceModel.uuid;

  return (
    <group name={`space-${spaceModel.uuid ?? 'unknown'}`}>
      <Floor shape={shape} selectedRoom={selected} onClick={onRoomClick} floorModel={floorModel} roomModel={roomModel} writeGeometryAttributes={true} spaceId={spaceModel.uuid} />

      <mesh onPointerDown={onFloorPointerDown?.(shape)} onPointerMove={onFloorPointerMove?.(shape)}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {indicatorData.length > 0 && (
        <group name={`space-angleIndicators-${spaceModel.uuid}`}>
          {indicatorData.map((data) => (
            <React.Fragment key={data.key}>
              {showAngles && (
                <WallAngleIndicator wall={data.prevEdge as any} nextWall={data.nextEdge as any} polygonOrientation={polygonOrientation} innerCorner={data.innerCorner} />
              )}

              {showWallInnerLength && (
                <InnerWallLengthIndicator
                  startPoint={data.startPoint}
                  endPoint={data.endPoint}
                  wallSideALength={data.wallSideALength}
                  shiftIndicator={data.shiftIndicator}
                  isWallSelected={data.isWallSelected}
                  wallSide={data.wallSide as any}
                  polygonOrientation={polygonOrientation}
                />
              )}
              {data.hasElements && showElementDistances && data.segWall && data.inwardNormal && (
                <ElementDistanceIndicator
                  windows={data.segWall.windows || []}
                  doors={data.segWall.doors || []}
                  passages={data.segWall.passages || []}
                  visualStartPoint={data.startPoint}
                  visualEndPoint={data.endPoint}
                  inwardNormal={data.inwardNormal}
                />
              )}
            </React.Fragment>
          ))}
        </group>
      )}
    </group>
  );
};

export default Space;
